import api from './client';
import type {
  OrgCreate,
  OrgUpdate,
  OrgResponse,
  OrgMemberAdd,
  ListParams,
} from './types';

export const organizationsApi = {
  /**
   * List all organizations
   * @param params - Query parameters (limit)
   */
  list: async (params?: ListParams): Promise<OrgResponse[]> => {
    const { data } = await api.get('/organizations', { params });
    return data;
  },

  /**
   * Get a specific organization by ID
   * @param orgId - Organization ID
   */
  get: async (orgId: string): Promise<OrgResponse> => {
    const { data } = await api.get(`/organizations/${orgId}`);
    return data;
  },

  /**
   * Create a new organization
   * @param org - Organization data
   */
  create: async (org: OrgCreate): Promise<OrgResponse> => {
    const { data } = await api.post('/organizations', org);
    return data;
  },

  /**
   * Update an existing organization
   * @param orgId - Organization ID
   * @param updates - Fields to update
   */
  update: async (orgId: string, updates: OrgUpdate): Promise<OrgResponse> => {
    const { data } = await api.patch(`/organizations/${orgId}`, updates);
    return data;
  },

  /**
   * Delete an organization
   * @param orgId - Organization ID
   */
  delete: async (orgId: string): Promise<void> => {
    await api.delete(`/organizations/${orgId}`);
  },

  /**
   * Add a member to an organization
   * @param orgId - Organization ID
   * @param memberData - Member data (user_id, role)
   */
  addMember: async (orgId: string, memberData: OrgMemberAdd): Promise<void> => {
    const { data } = await api.post(`/organizations/${orgId}/members`, memberData);
    return data;
  },

  /**
   * Remove a member from an organization
   * @param orgId - Organization ID
   * @param userId - User ID to remove
   */
  removeMember: async (orgId: string, userId: string): Promise<void> => {
    await api.delete(`/organizations/${orgId}/members/${userId}`);
  },

  /**
   * Upload logo for an organization
   * @param orgId - Organization ID
   * @param file - Image file to upload
   * @returns Object with logo_url and message
   */
  uploadLogo: async (orgId: string, file: File): Promise<{ logo_url: string; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await api.post(`/organizations/${orgId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },
};

/** A period during which the WhatsApp session was down — inbound client messages
 *  sent in this window never reached the app and cannot be recovered. */
export type WhatsAppDowntimeGap = {
  id: string;
  started_at: string;
  ended_at: string | null;
  open: boolean;
  session_status: string;
  duration_minutes?: number | null;
  acknowledged: boolean;
};

export type WhatsAppSessionStatus = {
  status: string;
  label: string;
  connected: boolean;
  /** The org's kill switch (Réglages → WhatsApp). False = every send is blocked
   *  server-side on purpose; distinct from a disconnect, which is a failure. */
  sending_enabled: boolean;
  /** Session is dead and will NOT self-heal — needs a QR re-scan. */
  critical: boolean;
  needs_scan: boolean;
  /** False when no WasenderAPI personal token is configured (QR only on wasenderapi.com). */
  can_reconnect_in_app: boolean;
  manage_url: string;
  down_since: string | null;
  last_check_at?: string | null;
  checked_at: string;
  cached?: boolean;
  gaps: WhatsAppDowntimeGap[];
};

export type WhatsAppQrResponse = {
  available: boolean;
  qr?: string;
  reason?: string;
  session_id?: string;
  manage_url: string;
};

// WhatsApp-specific API
export const whatsappApi = {
  connect: async (data: {
    provider?: "wasender" | "twilio";
    twilio_phone_number?: string;
    wasender_api_key?: string;
    wasender_session_id?: string;
    phone_number?: string;
    business_name?: string;
    display_phone?: string;
  }): Promise<{ status: string; provider: string }> => {
    const { data: result } = await api.post('/whatsapp/connect', data);
    return result;
  },

  disconnect: async (): Promise<void> => {
    await api.post('/whatsapp/disconnect');
  },

  setProvider: async (provider: "wasender" | "twilio"): Promise<{ status: string; provider: string }> => {
    const { data } = await api.post('/whatsapp/set-provider', { provider });
    return data;
  },

  getDailyStats: async (
    days: number = 30
  ): Promise<
    { date: string; message_count: number; total_cost: number }[]
  > => {
    const { data } = await api.get('/whatsapp/spending/daily', {
      params: { days },
    });
    return data;
  },

  getSpending: async (): Promise<{
    solde: number;
    total_spent: number;
    transaction_count: number;
  }> => {
    const { data } = await api.get('/whatsapp/spending');
    return data;
  },

  getTransactions: async (
    limit: number = 50,
    offset: number = 0
  ): Promise<
    {
      id: string;
      org_id: string;
      discussion_id: string;
      message_sid: string;
      direction: string;
      message_type: string;
      unit_cost: number;
      created_at: string;
    }[]
  > => {
    const { data } = await api.get('/whatsapp/transactions', {
      params: { limit, offset },
    });
    return data;
  },

  // --- Session health (banner + reconnection) ---

  /** Live session state. `force` bypasses the ~45s server cache (after a re-scan). */
  getSessionStatus: async (force = false): Promise<WhatsAppSessionStatus> => {
    const { data } = await api.get('/whatsapp/session-status', { params: { force } });
    return data;
  },

  /** Fresh QR code to re-link the session. `available: false` = configure it on wasenderapi.com. */
  getQr: async (): Promise<WhatsAppQrResponse> => {
    const { data } = await api.get('/whatsapp/qr');
    return data;
  },

  /** Dismiss a "messages possibly missed" warning once someone has checked WhatsApp. */
  acknowledgeGap: async (gapId: string): Promise<void> => {
    await api.post('/whatsapp/downtime-gaps/acknowledge', { gap_id: gapId });
  },

  // --- Templates ---

  listTemplates: async (): Promise<WhatsAppTemplate[]> => {
    const { data } = await api.get('/whatsapp/templates');
    return data;
  },

  createTemplate: async (template: CreateWhatsAppTemplate): Promise<WhatsAppTemplate> => {
    const { data } = await api.post('/whatsapp/templates', template);
    return data;
  },

  updateTemplate: async (id: string, updates: Partial<CreateWhatsAppTemplate>): Promise<WhatsAppTemplate> => {
    const { data } = await api.put(`/whatsapp/templates/${id}`, updates);
    return data;
  },

  deleteTemplate: async (id: string): Promise<void> => {
    await api.delete(`/whatsapp/templates/${id}`);
  },

  resubmitTemplate: async (id: string): Promise<WhatsAppTemplate> => {
    const { data } = await api.post(`/whatsapp/templates/${id}/resubmit`);
    return data;
  },

  syncTemplateStatuses: async (): Promise<{ updated_count: number; updated: WhatsAppTemplate[] }> => {
    const { data } = await api.post('/whatsapp/templates/sync-status');
    return data;
  },

  createDefaultTemplates: async (): Promise<{ created_count: number; templates: WhatsAppTemplate[] }> => {
    const { data } = await api.post('/whatsapp/templates/create-defaults');
    return data;
  },

  sendTemplate: async (params: {
    discussion_id: string;
    template_id: string;
    variable_values: Record<string, string>;
  }): Promise<any> => {
    const { data } = await api.post('/whatsapp/send-template', params);
    return data;
  },

  pollStatuses: async (discussionId: string): Promise<{
    updated: { msg_id: string; old_status: string; new_status: string }[];
    count: number;
  }> => {
    const { data } = await api.get(`/whatsapp/poll-statuses/${discussionId}`);
    return data;
  },
};

// Email spending API
export const emailApi = {
  getSpending: async (): Promise<{
    solde: number;
    total_spent: number;
    transaction_count: number;
  }> => {
    const { data } = await api.get('/email/spending');
    return data;
  },

  getDailyStats: async (
    days: number = 30
  ): Promise<
    { date: string; message_count: number; total_cost: number }[]
  > => {
    const { data } = await api.get('/email/spending/daily', {
      params: { days },
    });
    return data;
  },

  getTransactions: async (
    limit: number = 50,
    offset: number = 0
  ): Promise<
    {
      id: string;
      org_id: string;
      source: string;
      reference_id: string;
      recipient_count: number;
      unit_cost: number;
      total_cost: number;
      created_at: string;
    }[]
  > => {
    const { data } = await api.get('/email/transactions', {
      params: { limit, offset },
    });
    return data;
  },

};

// Communications unified API (single balance across all channels)
export const communicationsApi = {
  getSpending: async (): Promise<{
    solde: number;
    total_spent: number;
    whatsapp: { total_spent: number; transaction_count: number };
    email: { total_spent: number; transaction_count: number };
    signatures: { total_spent: number; transaction_count: number };
    ai: { total_spent: number; transaction_count: number };
  }> => {
    const { data } = await api.get('/communications/spending');
    return data;
  },

  getDailyStats: async (
    days: number = 30
  ): Promise<
    {
      date: string;
      whatsapp_count: number;
      whatsapp_cost: number;
      email_count: number;
      email_cost: number;
      signature_count: number;
      signature_cost: number;
      ai_count: number;
      ai_cost: number;
      total_cost: number;
    }[]
  > => {
    const { data } = await api.get('/communications/spending/daily', {
      params: { days },
    });
    return data;
  },

  createTopupLink: async (amount: number): Promise<{
    payment_link_url: string;
    amount: number;
    reference: string;
  }> => {
    const { data } = await api.post('/communications/topup-link', { amount });
    return data;
  },
};

// Signature spending API
export const signatureSpendingApi = {
  getSpending: async (): Promise<{
    solde: number;
    total_spent: number;
    transaction_count: number;
    signature_level: string;
  }> => {
    const { data } = await api.get('/signatures/spending');
    return data;
  },

  getDailyStats: async (
    days: number = 30
  ): Promise<
    { date: string; signature_count: number; total_cost: number }[]
  > => {
    const { data } = await api.get('/signatures/spending/daily', {
      params: { days },
    });
    return data;
  },

  getTransactions: async (
    limit: number = 50,
    offset: number = 0
  ): Promise<
    {
      id: string;
      org_id: string;
      doc_id: string;
      signer_name: string;
      signature_type: string;
      level: string;
      unit_cost: number;
      created_at: string;
    }[]
  > => {
    const { data } = await api.get('/signatures/transactions', {
      params: { limit, offset },
    });
    return data;
  },
};

// AI spending API
export const aiSpendingApi = {
  getSpending: async (): Promise<{
    solde: number;
    total_spent: number;
    transaction_count: number;
  }> => {
    const { data } = await api.get('/ai/spending');
    return data;
  },

  getDailyStats: async (
    days: number = 30
  ): Promise<
    { date: string; call_count: number; total_cost: number }[]
  > => {
    const { data } = await api.get('/ai/spending/daily', {
      params: { days },
    });
    return data;
  },

  getTransactions: async (
    limit: number = 50,
    offset: number = 0
  ): Promise<
    {
      id: string;
      org_id: string;
      call_type: string;
      reference_id: string;
      model: string;
      unit_cost: number;
      created_at: string;
    }[]
  > => {
    const { data } = await api.get('/ai/transactions', {
      params: { limit, offset },
    });
    return data;
  },
};

// --- WhatsApp Template types ---

export interface WhatsAppTemplate {
  id: string;
  org_id: string;
  name: string;
  friendly_name: string;
  body: string;
  variables: Record<string, string>;
  buttons: { type: string; title: string; url?: string; phone?: string }[];
  quick_replies: string[];
  category: string;
  content_sid: string | null;
  approval_status: "pending" | "approved" | "rejected" | "error" | "paused";
  created_at: string;
  updated_at: string;
}

export interface CreateWhatsAppTemplate {
  name: string;
  friendly_name?: string;
  body: string;
  variables?: Record<string, string>;
  buttons?: { type: string; title: string; url?: string }[];
  quick_replies?: string[];
  category?: string;
}

